// useRoster — the Interview Coverage roster (function → target interview count → assigned
// interviewees) for a project. It is viz-owned and consultant-editable, stored via
// /api/synthesis-viz/:projectId/roster. This is the planned side of coverage — "who do we still
// need to talk to" — which exists nowhere in the engine tables (per spec). With no projectId or
// before anything is saved the roster is empty and the Coverage tab falls back to the synthesis
// payload's own coverage rows, so nothing regresses.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../api';

export interface RosterInterviewee {
  name: string;
  role?: string;
  interviewed?: boolean;
}
export interface RosterFn {
  fn: string;
  target: number;
  interviewees: RosterInterviewee[];
}

export interface RosterData {
  roster: RosterFn[];
  loading: boolean;
  saving: boolean;
  editable: boolean;               // false when there is no project to persist against
  setRoster: (r: RosterFn[]) => void;
  save: (r: RosterFn[]) => Promise<boolean>;
}

export function useRoster(projectId?: string | null): RosterData {
  const [roster, setRoster] = useState<RosterFn[]>([]);
  const [loading, setLoading] = useState(!!projectId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId) { setRoster([]); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await apiClient.get(`/api/synthesis-viz/${encodeURIComponent(projectId)}/roster`);
        const live = res?.data?.roster;
        if (alive) setRoster(Array.isArray(live) ? live : []);
      } catch {
        if (alive) setRoster([]);      // no route / no row — empty roster, never an error surface
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const save = useCallback(async (r: RosterFn[]) => {
    setRoster(r);
    if (!projectId) return false;      // nothing to persist against; keep the local edit only
    setSaving(true);
    try {
      await apiClient.put(`/api/synthesis-viz/${encodeURIComponent(projectId)}/roster`, { roster: r });
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  return { roster, loading, saving, editable: !!projectId, setRoster, save };
}
